import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Box, Button, Checkbox, Chip, Fade, FormControlLabel, Grid, LinearProgress, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material'
import { NumericFormat } from 'react-number-format'
import gaiaTheme from '../../../root-components/theme'
import { AnalyticMetric } from '../analytics-components/graphs/analytic-metric'
import { ReductionGraph } from './reduction-graph'
import { IReductionTargetGraphData, IReductionTargetGraphMetric, IReductionTargetGraphResponse } from '../../../shared/services/reduction-target/reduction-target.model'
import ReductionTargetService from '../../../shared/services/reduction-target/reduction-target.service'
import TargetSettingService from '../../../shared/services/target-setting/target-setting.service'
import { ITargetSetting } from '../../../shared/services/target-setting/target-setting.model'
import { ReductionTargetSkeleton } from '../../pages/reduction/reduction-target-skeleton'
import { useSkeletonDelay } from '../../../hooks/use-skeleton-delay'
import { UserDetailsContext } from '../../../context/user-details/user-details'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { TooltipButton } from '../miscellaneous-components/tooltip-button'
import { EmissionBasisToggle } from '../analytics-components/emission-basis-toggle'
import {
  reductionScopeTwoDataTypeFromDefaultMarketBased,
  type ReductionScopeTwoDataType,
} from '../../../shared/utils/default-market-based-utils'

type Scope = 'total' | '1' | '2' | '3'
type DataType = ReductionScopeTwoDataType

export type ReductionVisualDisplayProps = {
  /** Refetch page state after the user resets the plan (e.g. parent shows create form again). */
  onPlanReset?: () => void
}

export const ReductionVisualDisplay = ({ onPlanReset }: ReductionVisualDisplayProps) => {
  const reductionTargetService = new ReductionTargetService()
  const targetSettingService = new TargetSettingService()
  const userDetailsContext = useContext(UserDetailsContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const organisationDefaultMarketBased = userDetailsContext.userDetails?.organisation?.defaultMarketBased

  const [targetSettings, setTargetSettings] = useState<ITargetSetting | null>(null)
  const [scope, setScope] = useState<Scope>('total')
  const [dataType, setDataType] = useState<DataType>(() =>
    reductionScopeTwoDataTypeFromDefaultMarketBased(organisationDefaultMarketBased)
  )
  const [graphResponse, setGraphResponse] = useState<IReductionTargetGraphResponse | null>(null)
  const [isLoadingGraph, setIsLoadingGraph] = useState(true)
  // Scenarios are what-if comparisons; default them off to keep the graph uncluttered.
  const [shownScenarioIds, setShownScenarioIds] = useState<string[]>([])
  const [showCombinedScenario, setShowCombinedScenario] = useState(false)

  const hasLocationMarketToggle = scope === 'total' || scope === '2'
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'))
  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'))

  const dataTypeKey = dataType === 'market-based' ? 'marketBased' : 'locationBased'
  const rawGraph = graphResponse?.graph
  const graphData: IReductionTargetGraphData[] = !rawGraph
    ? []
    : Array.isArray(rawGraph)
      ? rawGraph
      : (rawGraph as { marketBased?: IReductionTargetGraphData[]; locationBased?: IReductionTargetGraphData[] })?.[dataTypeKey] ?? []
  const rawMetrics = graphResponse?.metrics
  const graphMetrics: IReductionTargetGraphMetric | undefined = !rawMetrics
    ? undefined
    : hasLocationMarketToggle && rawMetrics && 'marketBased' in rawMetrics
      ? (rawMetrics as { marketBased: IReductionTargetGraphMetric; locationBased: IReductionTargetGraphMetric })[dataTypeKey]
      : (rawMetrics as IReductionTargetGraphMetric)

  const scenarioSeries = graphResponse?.scenarios ?? []
  const combinedScenario = graphResponse?.combinedScenario ?? null
  const visibleScenarioSeries = [
    ...scenarioSeries.filter((s) => shownScenarioIds.includes(s.id)),
    ...(showCombinedScenario && combinedScenario ? [combinedScenario] : []),
  ]
  const toggleScenarioVisibility = (id: string) => {
    setShownScenarioIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const scopeLabel = scope === 'total' ? '' : `Scope ${scope} `
  const dataTypeLabel = hasLocationMarketToggle ? ` (${dataType.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('-')})` : ''
  const isLoading = isLoadingGraph || targetSettings == null
  const showSkeleton = useSkeletonDelay(isLoading, 250)

  const actionCoverage = useMemo(() => {
    if (!graphMetrics) return { percent: 0, planned: 0, gap: 0 }
    const latestActual = graphMetrics.latestActualValue ?? graphMetrics.current?.totalEmission ?? 0
    const finalTarget = graphMetrics.finalTargetValue ?? graphMetrics.target?.totalEmission ?? 0
    const planned = graphMetrics.plannedReductionByTarget ?? 0
    const gap = graphMetrics.gapToTarget ?? 0
    const needed = Math.max(latestActual - finalTarget, 0)
    const percent = needed > 0 ? Math.min(Math.round((planned / needed) * 100), 100) : 0
    return { percent, planned, gap }
  }, [graphMetrics])

  const scopeHandler = (e: React.MouseEvent<HTMLElement>, newScope: Scope | null) => {
    if (newScope !== null) setScope(newScope)
  }

  const resetReductionPlan = () => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Reset Reduction Plan',
      message: 'Are you sure you want to reset your reduction plan? This will delete your current targets and allow you to reconfigure. This action cannot be undone.',
      buttonCopy: 'Reset Plan',
      cancelButtonCopy: 'Cancel',
    }).then((response) => {
      if (response) {
        targetSettingService
          .deleteTargetSetting()
          .then(() => {
            userNotificationsContext.openSnackbar({ open: true, message: 'Reduction plan reset successfully' })
            onPlanReset?.()
          })
          .catch((err) => {
            userNotificationsContext.openSnackbar({
              open: true,
              message: 'There was an error resetting the plan, please try again',
              backgroundColor: 'text.primary',
            })
            console.log(err)
          })
      }
    })
  }

  useEffect(() => {
    setDataType(reductionScopeTwoDataTypeFromDefaultMarketBased(organisationDefaultMarketBased))
  }, [organisationDefaultMarketBased])

  useEffect(() => {
    targetSettingService
      .getTargetSettings()
      .then((response) => {
        const row = Array.isArray(response) ? response[0] : response
        setTargetSettings(row ?? null)
      })
      .catch((err) => console.log(err))
  }, [])

  useEffect(() => {
    setGraphResponse(null)
    setIsLoadingGraph(true)
    const scopeTwoMethod = hasLocationMarketToggle ? dataType : undefined
    reductionTargetService
      .getReductionTargetsGraphData(scope, scopeTwoMethod)
      .then((response) => {
        setGraphResponse(response)
        setIsLoadingGraph(false)
      })
      .catch((err) => {
        console.log(err)
        setIsLoadingGraph(false)
      })
  }, [scope, dataType])

  if (isLoading) {
    return showSkeleton ? <ReductionTargetSkeleton /> : null
  }

  return (
    <>
      <Stack
        direction={isMobileModeMd ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobileModeMd ? 'flex-start' : 'center'}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            value={scope}
            onChange={scopeHandler}
            exclusive
            aria-label="scope"
            orientation={isMobileModeSm ? 'vertical' : 'horizontal'}
          >
            <ToggleButton value="total" sx={{ width: !isMobileModeSm ? '100px' : '100%', pt: 0.75, pb: 0.75 }}>
              <Typography variant="body1" sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Total</Typography>
            </ToggleButton>
            <ToggleButton value="1" sx={{ width: !isMobileModeSm ? '100px' : '100%', pt: 0.75, pb: 0.75 }}>
              <Typography variant="body1" sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Scope 1</Typography>
            </ToggleButton>
            <ToggleButton value="2" sx={{ width: !isMobileModeSm ? '100px' : '100%', pt: 0.75, pb: 0.75 }}>
              <Typography variant="body1" sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Scope 2</Typography>
            </ToggleButton>
            <ToggleButton value="3" sx={{ width: !isMobileModeSm ? '100px' : '100%', pt: 0.75, pb: 0.75 }}>
              <Typography variant="body1" sx={{ color: 'text.primary', textTransform: 'capitalize' }}>Scope 3</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button variant="outlined" color="error" size="small" onClick={resetReductionPlan} sx={{ textTransform: 'none' }}>
            Reset Plan
          </Button>
        </Stack>
        {hasLocationMarketToggle && (
          <EmissionBasisToggle
            value={dataType === 'market-based' ? 'marketBased' : 'locationBased'}
            onChange={(next) => setDataType(next === 'marketBased' ? 'market-based' : 'location-based')}
            orientation={isMobileModeMd ? 'vertical' : 'horizontal'}
            fullWidth={isMobileModeMd}
            buttonSx={{ width: !isMobileModeMd ? '175px' : '100%', pt: 0.75, pb: 0.75 }}
            sx={{ width: isMobileModeMd ? '100%' : 'auto' }}
          />
        )}
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          px: 3,
          py: 1.5,
          mb: 2,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          <b>Base Year:</b> {targetSettings.baseYear}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <b>Target Year:</b> {targetSettings.targetYear}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <b>Method:</b> {targetSettings.calculationMethod === '1.5c' ? '1.5°C' : 'Custom'}
        </Typography>
        {targetSettings.annualGrowthPercentage != null && targetSettings.annualGrowthPercentage !== 0 && (
          <Typography variant="body2" color="text.secondary">
            <b>Annual Growth:</b> {targetSettings.annualGrowthPercentage}%
          </Typography>
        )}
        {targetSettings.calculationMethod === 'custom' && !isMobileModeSm && (
          <Typography variant="body2" color="text.secondary">
            S1 ({targetSettings.scopeOneReductionPercentage ?? '-'}%)
            {' · '}S2 MB ({targetSettings.scopeTwoMarketReductionPercentage ?? '-'}%)
            {' · '}S2 LB ({targetSettings.scopeTwoLocationReductionPercentage ?? '-'}%)
            {' · '}S3 ({targetSettings.scopeThreeReductionPercentage ?? '-'}%)
          </Typography>
        )}
      </Paper>

      {isLoadingGraph ? (
        <Fade in={showSkeleton} unmountOnExit>
          <div>
            <ReductionTargetSkeleton />
          </div>
        </Fade>
      ) : (
        <Fade in={!showSkeleton} unmountOnExit>
          <div>
            <Grid container>
              <Stack display="flex" direction={isMobileModeMd ? 'column' : 'row'} spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xl={4} lg={4} md={4} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column', paddingLeft: '0 !important' }}>
                    <AnalyticMetric
                      title={`Current ${scopeLabel}Emissions${dataTypeLabel}, at ${graphMetrics?.latestActualYear ?? graphMetrics?.current?.year ?? ''}`}
                      value={graphMetrics?.latestActualValue ?? graphMetrics?.current?.totalEmission ?? 0}
                      units="tCO2e"
                    />
                  </Grid>
                  <Grid item xl={4} lg={4} md={4} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <AnalyticMetric
                      units="tCO2e"
                      title={`Target ${scopeLabel}Emissions${dataTypeLabel}, by ${graphMetrics?.finalTargetYear ?? graphMetrics?.target?.year ?? ''}`}
                      value={graphMetrics?.finalTargetValue ?? graphMetrics?.target?.totalEmission ?? 0}
                    />
                  </Grid>
                  {graphResponse && (
                    <Grid item xl={4} lg={4} md={4} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Paper variant="outlined" sx={{ p: 4, width: '100%', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Stack direction="row" alignItems="center">
                            <Typography variant="subtitle1">
                              <b>Planned Reduction Coverage</b>
                            </Typography>
                            <TooltipButton
                              text="Shows how much of the required emission reduction is covered by your planned actions. 100% means your planned actions fully bridge the gap between current and target emissions."
                              noPadding={true}
                              placement="right"
                            />
                          </Stack>
                          <Typography variant="h6" fontWeight={700}>
                            {actionCoverage.percent}%
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={actionCoverage.percent}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: 'action.hover',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 5,
                              backgroundColor: actionCoverage.percent >= 100 ? 'success.main' : 'primary.main',
                            },
                          }}
                        />
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Planned:{' '}
                            <NumericFormat
                              value={actionCoverage.planned}
                              displayType="text"
                              thousandSeparator=","
                              decimalScale={0}
                            />{' '}
                            tCO2e
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {actionCoverage.gap > 0 ? 'Gap' : 'Surplus'}:{' '}
                            <NumericFormat
                              value={Math.abs(actionCoverage.gap)}
                              displayType="text"
                              thousandSeparator=","
                              decimalScale={0}
                            />{' '}
                            tCO2e
                          </Typography>
                        </Stack>
                      </Paper>
                    </Grid>
                  )}
                  <Grid item xl={12} lg={12} md={12} sm={12} xs={12} sx={{ paddingLeft: '0 !important' }}>
                    <Paper variant="outlined" sx={{ p: 4, borderRadius: '10px' }}>
                      {scenarioSeries.length > 0 && (
                        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Scenarios:</Typography>
                          {scenarioSeries.map((s) => {
                            const isVisible = shownScenarioIds.includes(s.id)
                            return (
                              <Chip
                                key={s.id}
                                label={s.name}
                                size="small"
                                variant={isVisible ? 'filled' : 'outlined'}
                                color={isVisible ? 'primary' : 'default'}
                                onClick={() => toggleScenarioVisibility(s.id)}
                              />
                            )
                          })}
                        </Stack>
                      )}
                      <Box>
                        <ReductionGraph graphData={graphData} includeCurrent={true} scenarioSeries={visibleScenarioSeries} chartHeight={400} annualGrowthPercentage={targetSettings.annualGrowthPercentage} />
                      </Box>
                      {combinedScenario && (
                        <FormControlLabel
                          sx={{ mt: 0.5 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={showCombinedScenario}
                              onChange={(e) => setShowCombinedScenario(e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2" color="text.secondary">
                              Show all scenarios combined: one line with every scenario&apos;s measures applied together
                            </Typography>
                          }
                        />
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Stack>
            </Grid>
          </div>
        </Fade>
      )}
    </>
  )
}
